import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Filter, CalendarDays, MapPin, Clock, User } from 'lucide-react'
import { besuche as besucheApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { besuchStatusBadge } from '../../components/ui/Badge'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { formatDatum, BESUCH_TYP_LABEL } from '../../lib/utils'
import { cn } from '../../lib/utils'
import type { BesuchStatus } from '../../types'

const STATUS_TABS: { label: string; value: BesuchStatus | 'alle' }[] = [
  { label: 'Alle', value: 'alle' },
  { label: 'Geplant', value: 'geplant' },
  { label: 'Unterwegs', value: 'unterwegs' },
  { label: 'Abgeschlossen', value: 'abgeschlossen' },
]

export function BesuchePlanungPage() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<BesuchStatus | 'alle'>('alle')

  const { data, isLoading } = useQuery({
    queryKey: ['besuche', statusFilter],
    queryFn: () => besucheApi.list(statusFilter !== 'alle' ? { status: statusFilter } : {}),
  })

  const grouped = groupByDate(data ?? [])

  return (
    <>
      <TopBar
        title="Besuchsplanung"
        rightAction={
          <Button icon={<Plus size={18} />} size="sm" onClick={() => navigate('/besuche/neu')}>
            Neu
          </Button>
        }
      />

      {/* Status Tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-none">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setStatusFilter(t.value)}
            className={cn(
              'shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors',
              statusFilter === t.value
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 space-y-4 pb-6">
        {isLoading ? (
          <LoadingSpinner />
        ) : Object.keys(grouped).length === 0 ? (
          <EmptyState
            icon={<CalendarDays size={28} />}
            title="Keine Besuche"
            description="Neuen Besuch planen"
            action={<Button icon={<Plus size={16} />} onClick={() => navigate('/besuche/neu')}>Besuch planen</Button>}
          />
        ) : (
          Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                {formatDatum(date, 'EEEE, d. MMMM')}
              </h3>
              <div className="space-y-2">
                {items.map((b) => (
                  <Card key={b.id} clickable onClick={() => navigate(`/besuche/${b.id}`)} className="flex gap-3 items-start">
                    <div className="shrink-0">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                        <CalendarDays size={18} className="text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-semibold text-gray-900 truncate text-sm">{b.kunde_name}</p>
                        {besuchStatusBadge(b.status)}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin size={10} />{b.kunde_ort ?? '–'}
                        </span>
                        {b.uhrzeit_start && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={10} />{b.uhrzeit_start}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <User size={10} />{b.techniker}
                        </span>
                      </div>
                      <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {BESUCH_TYP_LABEL[b.typ] ?? b.typ}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

function groupByDate<T extends { datum: string }>(items: T[]): Record<string, T[]> {
  return items.reduce<Record<string, T[]>>((acc, item) => {
    const key = item.datum.slice(0, 10)
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}
